import { animate, style, transition, trigger } from '@angular/animations';
import { Component } from '@angular/core';
import { IonicPage, ModalController, NavController, NavParams, Platform, ToastController } from 'ionic-angular';
import { DailyLogOperationInstance, LogComment } from '../../../models/projects/operation.models';
import { DateService } from '../../../services/date/date.service';
import { NavHelperService } from '../../../services/nav/nav-helper.service';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { cloneDeep } from 'lodash';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { TimeZoneService } from '../../../services/time-zone/time-zone.service';
import { LockedDayPermissions } from '../../../models/permissions/permission.models';
import { AppConfig } from '../../../app/app.config';
import { DailyLogModalPage } from '../../daily-log/daily-log-modal/daily-log-modal';
import { SupportContextKey } from '../../../models/support/support.models';
import { SupportContextService } from '../../../services/support/support-context.service';

@IonicPage()
@Component({
  selector: 'page-operation-generic-comments',
  templateUrl: 'operation-generic-comments.html',
  animations: [
    trigger('commentRowAnimation', [
      transition(':enter', [
        style({
          height: 0,
          opacity: 0,
          overflow: 'hidden'
        }),
        animate('260ms cubic-bezier(0.2, 0, 0, 1)', style({
          height: '*',
          opacity: 1,
          overflow: 'hidden'
        }))
      ]),
      transition(':leave', [
        style({
          height: '*',
          opacity: 1,
          overflow: 'hidden'
        }),
        animate('180ms ease-in', style({
          height: 0,
          opacity: 0,
          overflow: 'hidden'
        }))
      ])
    ])
  ]
})

export class OperationGenericCommentsPage {
  config = AppConfig.settings;
  operationInstance: DailyLogOperationInstance = new DailyLogOperationInstance();
  isWeb: boolean;
  min: string;
  max: string;
  minDate: PtLuxonDate;
  maxDate: PtLuxonDate;
  tempComments: LogComment[];
  tempHighestCommentIdentifier: number;
  originalComments: LogComment[];
  loading: boolean = true;
  showError = false;
  dailyLogDate: PtLuxonDate;
  multipleDayOperation: boolean = false;

  displayInvalidTimesErrorMessage: boolean;
  commentTimeLostInTime: boolean;
  displayEmptyCommentWarning: boolean;

  lockedDayPermissions: LockedDayPermissions;

  constructor(public navCtrl: NavController,
              public navParams: NavParams,
              public platform: Platform,
              public dateService: DateService,
              public navHelperService: NavHelperService,
              private dailyLogService: DailyLogService,
              private timeZoneService: TimeZoneService,
              private toastCtrl: ToastController,
              private modalController: ModalController,
              private supportContextService: SupportContextService
  ) {

    this.operationInstance = navParams.get('operationInstance');
    this.dailyLogDate = navParams.get('dailyLogDate');
    this.lockedDayPermissions = navParams.get('lockedDayPermissions');

    if (!this.operationInstance.comments) {
      this.operationInstance.comments = [];
    }

    const opStartDay = this.operationInstance.startTime.getDay();
    const opEndDay = this.operationInstance.endTime.getDay();
    this.multipleDayOperation = opStartDay !== opEndDay;
  }

  ionViewDidEnter() {
    this.supportContextService.setComponentContext(
      SupportContextKey.EditOperation,
      'Operation Comments',
      'OperationGenericCommentsPage',
      this.getOperationSupportDetails()
    );

    this.navHelperService.commentWarned = false;
    this.navHelperService.showCommentWarning = false;
    this.loading = false;
  }

  ionViewCanLeave(): boolean {
    this.displayInvalidTimesErrorMessage = false;
    this.displayEmptyCommentWarning = false;
    this.commentTimeLostInTime = false;

    this.checkCommentsChanged();

    if (this.navHelperService.unsavedComment && !this.navHelperService.commentWarned) {
      this.navHelperService.commentWarned = true;
      this.navHelperService.showCommentWarning = true;
      this.navHelperService.unsavedComment = false;
      return false;
    }

    return true;
  }

  async ionViewDidLoad() {
    this.tempComments = cloneDeep(this.operationInstance.comments);
    this.tempComments = await this.lockOtherDayComments(this.tempComments);
    this.tempHighestCommentIdentifier = this.operationInstance.highestCommentIdentifier
      ? cloneDeep(this.operationInstance.highestCommentIdentifier)
      : 1;
    this.originalComments = this.tempComments ? cloneDeep(this.tempComments) : [];

    if (this.tempComments.some(e => e.endTimeToShow == null || e.startTimeToShow == null)) {
      this.navHelperService.invalidComments = true;
    } else {
      this.navHelperService.invalidComments = false;
    }

    this.isWeb = this.platform.is('core');

    if (this.operationInstance.startTime.dateTime < this.operationInstance.endTime.getDate()) {
      this.min = this.operationInstance.startTimeToShow;
      this.max = this.operationInstance.endTimeToShow;
    } else {
      this.min = this.operationInstance.endTimeToShow;
      this.max = this.operationInstance.startTimeToShow;
    }

    if (!this.operationInstance.endTime || !this.operationInstance.endTime.dateTime) {
      this.min = this.operationInstance.startTimeToShow;
      this.max = '23:59';
    }

    this.minDate = cloneDeep(this.dailyLogDate);
    this.maxDate = this.lockedDayPermissions.functionalPermissions.canImpactFutureDay
      ? (this.operationInstance.endTime && this.operationInstance.endTime.dateTime
        ? this.operationInstance.endTime
        : cloneDeep(this.dailyLogDate))
      : cloneDeep(this.dailyLogDate);
  }

  async validateComments(): Promise<boolean> {
    for (const comment of this.tempComments) {
      const startTime = await this.dateService
        .setPtLuxonDateFromUserEnteredString(comment.startTimeToShow, comment.startTimeDate && comment.startTimeDate.dateTime
          ? comment.startTimeDate.dateTime
          : this.dailyLogDate.dateTime);
      const endTime = await this.dateService
        .setPtLuxonDateFromUserEnteredString(comment.endTimeToShow, comment.endTimeDate && comment.endTimeDate.dateTime
          ? comment.endTimeDate.dateTime
          : this.dailyLogDate.dateTime);

      if (startTime.dateTime
        && startTime.dateTime < this.operationInstance.startTime.dateTime) {
        return false;
      }

      if (this.operationInstance.endTime.dateTime
        && startTime.dateTime
        && startTime.dateTime > this.operationInstance.endTime.dateTime) {
        return false;
      }

      if (endTime.dateTime
        && endTime.dateTime < this.operationInstance.startTime.dateTime) {
        return false;
      }

      if (this.operationInstance.endTime.dateTime
        && endTime.dateTime
        && endTime.dateTime > this.operationInstance.endTime.dateTime) {
        return false;
      }
    }
    return true;
  }

  async save() {
    if (this.tempComments.some(e => (e.startTimeDate && e.startTimeDate.isLostInTime && e.startTimeDate.isLostInTime.result)
      || (e.endTimeDate && e.endTimeDate.isLostInTime && e.endTimeDate.isLostInTime.result))) {
      this.commentTimeLostInTime = true;
      setTimeout(() => {
        this.commentTimeLostInTime = false;
      }, 5000);
      return;
    }

    const result = await this.validateComments();

    if (!result) {
      this.showError = true;
      setTimeout(() => {
        this.showError = false;
      }, 10000);
      return;
    }

    this.navHelperService.unsavedChanges = true;

    if (this.tempComments.some(e =>
      (e.endTimeToShow == null || e.endTimeToShow === '') &&
      (e.startTimeToShow == null || e.startTimeToShow === ''))) {
      this.navHelperService.invalidComments = true;
    } else {
      this.navHelperService.invalidComments = false;
    }

    if (this.tempComments.some(e => !e.data || e.data === '' || e.data.trim() === '')) {
      this.displayEmptyCommentWarning = true;
      return;
    } else {
      this.displayEmptyCommentWarning = false;
    }

    if (this.navHelperService.invalidComments === true) {
      this.displayInvalidTimesErrorMessage = true;
    } else {
      this.operationInstance.comments = this.tempComments;
      this.operationInstance.highestCommentIdentifier = this.tempHighestCommentIdentifier;

      for (let i = 0; i < this.operationInstance.comments.length; i++) {
        const comment = this.operationInstance.comments[i];
        if (comment.startTimeToShow == null || comment.startTimeToShow.trim() === '') {
          comment.startTimeDate = null;
        }
        if (comment.endTimeToShow == null || comment.endTimeToShow.trim() === '') {
          comment.endTimeDate = null;
        }
      }

      this.navHelperService.unsavedComment = false;
      this.navHelperService.showCommentWarning = false;

      this.presentSaveToast().then(() => {
        this.navCtrl.pop();
      });
    }

    this.navHelperService.showCommentWarning = false;
  }

  commentChanged(comment: LogComment, position?: number) {
    this.tempComments[position] = comment;
    this.navHelperService.unsavedComment = true;

    if (!(this.checkCommentValid(comment))) {
      this.navHelperService.invalidComments = true;
    }
    this.navHelperService.unsavedComment = true;
  }

  timeChanged() {
    this.navHelperService.unsavedComment = true;
  }

  deleteComment(position: number) {
    this.tempComments.splice(position, 1);
    this.navHelperService.unsavedComment = true;
  }

  addComment() {
    this.navHelperService.invalidComments = true;
    const comment = new LogComment();
    comment.identifier = this.tempHighestCommentIdentifier;
    comment.milestone = false;
    this.tempHighestCommentIdentifier++;
    this.tempComments.unshift(comment);
    this.navHelperService.unsavedComment = true;
  }

  trackByComment(index: number, comment: LogComment): number | string {
    return comment.id || comment.recordApiId || comment.identifier || index;
  }

  async checkCommentValid(comment: LogComment): Promise<boolean> {
    const startDate = await this.dateService.setPtLuxonDateFromUserEnteredString(comment.startTimeToShow, comment.startTimeDate ? comment.startTimeDate.dateTime : null);
    const endDate = await this.dateService.setPtLuxonDateFromUserEnteredString(comment.endTimeToShow, comment.endTimeDate ? comment.endTimeDate.dateTime : null);

    if (!(startDate >= this.minDate && startDate <= this.maxDate)) {
      return false;
    }
    if (!(endDate >= this.minDate && endDate <= this.maxDate)) {
      return false;
    }

    return true;
  }

  // checks for differences between the original set of comment entries and the current comment entries
  checkCommentsChanged() {
    if (this.navHelperService.unsavedComment) {
      // check newly added comments
      for (let i = 0; i < this.tempComments.length; i++) {
        const index = this.originalComments.findIndex(e => e.identifier === this.tempComments[i].identifier);
        if (index === -1) {
          this.navHelperService.unsavedComment = true;
        } else {
          this.checkCommentChanged(this.tempComments[i], this.originalComments[i]);
        }
      }
      // check for deleted comments
      for (let i = 0; i < this.originalComments.length; i++) {
        const index = this.tempComments.findIndex(e => e.identifier === this.originalComments[i].identifier);
        if (index === -1) {
          this.navHelperService.unsavedComment = true;
        }
      }
    }
  }

  checkCommentChanged(originalComment: LogComment, tempComment: LogComment) {
    if (originalComment.data !== tempComment.data
      || originalComment.appear !== tempComment.appear
      || originalComment.milestone !== tempComment.milestone
      || originalComment.endTimeToShow !== tempComment.endTimeToShow
      || originalComment.startTimeToShow !== tempComment.startTimeToShow
    ) {
      this.navHelperService.unsavedComment = true;
    } else {
      this.navHelperService.unsavedComment = false;
    }
  }

  presentSaveToast(): Promise<any> {
    const toast = this.toastCtrl.create({
      message: 'Comments Saved',
      duration: 3000,
      position: 'bottom'
    });

    return toast.present();
  }

  async lockOtherDayComments(comments: LogComment[]): Promise<LogComment[]> {
    const endOfDay = await this.dateService.getEndOfDayLocally(this.dailyLogDate.dateTime);
    comments.forEach(comment => {
      if (comment.startTimeDate.dateTime < this.dailyLogDate.dateTime || comment.startTimeDate.dateTime > endOfDay.dateTime) {
        comment.locked = true;
      }
    });

    return comments;
  }

  openDailyLogModal() {
    const dailyLogModal = this.modalController.create(DailyLogModalPage);
    dailyLogModal.present();
  }

  private getOperationSupportDetails(): {[key: string]: string} {
    return {
      instanceId: this.getOperationInstanceId(),
      operationName: this.getOperationName()
    };
  }

  private getOperationInstanceId(): string {
    if (!this.operationInstance) {
      return '';
    }

    return this.operationInstance.id != null
      ? `${this.operationInstance.id}`
      : this.operationInstance.localId != null
        ? `${this.operationInstance.localId}`
        : '';
  }

  private getOperationName(): string {
    if (!this.operationInstance || !this.operationInstance.projectSpecificOperation) {
      return '';
    }

    return this.operationInstance.projectSpecificOperation.name
      || this.operationInstance.projectSpecificOperation.operationSummary?.name
      || '';
  }
}



// WEBPACK FOOTER //
// ./src/pages/operations/operation-generic-comments/operation-generic-comments.ts